import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormSpy } from "react-final-form";
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormContext } from "../../context/form-context";
import { setSelectedBaby } from '../../actions/ui';
import { add } from "date-fns";

import { required } from "../../helpers/formValidators";
import { addDateAndTimeFields, getDurationStrict } from "./Duration";
import {
  FormLabel,
  FormInputHolder,
  FormSelect,
  FormRadio,
  FormIconChooser,
  BaseForm,
} from "../form";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import { PrimaryButton, SecondaryButton } from "../buttons";
import babyIcons from "../icons/babyIcons";
import { Condition, Error } from "../form/BaseForm";
import { OnChange } from "react-final-form-listeners";

import Pumping from "./FieldExcerpts/Pumping";
import Feeding from "./FieldExcerpts/Feeding";
import Diaper from "./FieldExcerpts/Diaper";

const DietDiaperForm = props => {
  const {
    selectedProfile,
    babies,
    profileOptions,
    dietDiaperRecordsPatch,
    dietDiaperRecordsPost,
    dietDiaperRecordsList = () => Promise.resolve(), 
    accountId,
    handleRecords,
    showFirstTimeSavingModal,
    showUnSavedModal,
    hideTrackerModals,
    showSaveEntryModal,
    selectedBabyId = null,
    dietDiaperModalShown = false
  } = props;

  const [initialValues, setInitialValues] = useState(null);
  const formContext = useContext(FormContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const defaultValues = {
    babyId: selectedBabyId,
    providerId: selectedProfile.id,
    weight: "100.00",
    bodyLength: "10.00",
    headCircumference: "5.0",
    date: new Date(),
    startTime: new Date(),
    endTime: add(new Date(), { minutes: 10 })
  };

  useEffect(() => {
    if (!dietDiaperModalShown) {
      setInitialValues(null);
      return;
    }

    if (formContext.formValues && Object.keys(formContext.formValues).length > 0) {
      setInitialValues({ ...formContext.formValues });
      return;
    }

    const latestRecord = handleRecords('dietDiaper', selectedBabyId) || {};
    setInitialValues({ 
      ...defaultValues, 
      ...latestRecord,
      babyId: selectedBabyId 
    });
  }, [dietDiaperModalShown, selectedBabyId]);

  const toggleWarningModal = () => {
    return formContext.isDirty ? showUnSavedModal() : hideTrackerModals();
  };

  const onSubmit = (values) => {
    if (!values) return;

    const normalizeTrackerValue = (value) => {
      if (typeof value !== 'string') return value;
      return value.startsWith('tracker.') ? value.replace('tracker.', '') : value;
    };

    const normalizedActivityType = normalizeTrackerValue(values.activityType);
    const normalizedFeedingType = normalizeTrackerValue(values.feedingType);

    const hasDuration =
      normalizedActivityType === "pumping" ||
      (normalizedActivityType === "feeding" && normalizedFeedingType === "breast");

    const date = addDateAndTimeFields(values.date, values.startTime);
    const duration = hasDuration ? getDurationStrict(values.startTime, values.endTime) : 0;
    const endTime = hasDuration ? addDateAndTimeFields(values.date, values.endTime) : null;

    const recordBody = {
      ...values,
      activityType: normalizedActivityType,
      feedingType: normalizedFeedingType,
      date,
      duration,
      endTime,
      providerId: values.providerId === "other" ? null : values.providerId,
      otherProvider: values.providerId !== "other" ? null : values.otherProvider
    };

    const originalBabyId = initialValues?.babyId;
    const newBabyId = values.babyId || originalBabyId;

    if (values.id) {
      return dietDiaperRecordsPatch(
        { accountId: accountId, babyId: originalBabyId, id: values.id },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => {
          if (data) {
            dietDiaperRecordsList({ accountId, babyId: originalBabyId });
            if (newBabyId !== originalBabyId) {
              dietDiaperRecordsList({ accountId, babyId: newBabyId });
            }
            hideTrackerModals();
          }
        })
        .catch(() => <ErrorMessage />);
    } else {
      return dietDiaperRecordsPost(
        { accountId: accountId, babyId: newBabyId },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => {
          if (data) {
            dietDiaperRecordsList({ accountId, babyId: newBabyId });
            hideTrackerModals();
            dispatch(setSelectedBaby(newBabyId));
            navigate('/dashboard/diet-diaper');
          }
        })
        .catch(() => <ErrorMessage />);
    }
  };

  const onSave = (values) => {
    if (!showFirstTimeSavingModal) return onSubmit(values);
    formContext.setValues(values);
    formContext.setFunctions({ onSubmit });
    return showSaveEntryModal();
  };

  if (!dietDiaperModalShown || !initialValues) return null;

  return (
    <BaseForm 
      onSubmit={onSave} 
      initialValues={initialValues} 
      className="activity-tracker__form"
      enableReinitialize={true}
    >
      <h2>Track Diet & Diaper</h2>

      {babies && babies.length > 1 && !initialValues?.id && (
        <React.Fragment>
          <FormIconChooser>
            {babies.map(baby => (
              <FormRadio key={baby.id} value={baby.id} name="babyId" imageSrc={babyIcons[baby.icon]} validate={required}>
                {baby.name}
              </FormRadio>
            ))}
          </FormIconChooser>
          <OnChange name="babyId">
            {(value, previous) => {
              if (value === previous) return;
              const latestRecord =  handleRecords('dietDiaper', value) || {};
              setInitialValues({ ...initialValues, ...latestRecord, babyId: value });
            }}
          </OnChange>
          <Error name="babyId" />
        </React.Fragment>
      )}

      {babies && initialValues?.id && (() => {
        const editBaby = babies.find(b => b.id === initialValues.babyId);
        return editBaby ? (
          <FormIconChooser>
            <label className="form-radio form-radio--checked">
              <div className="form-radio__image-container">
                <img src={babyIcons[editBaby.icon]} alt={editBaby.name} className="form-radio__image" width="2.4rem" height="2.4rem" />
              </div>
              <span className="form-radio__label">{editBaby.name}</span>
            </label>
          </FormIconChooser>
        ) : null;
      })()}

      <FormLabel htmlFor="providerId">Provider</FormLabel>
      <FormSelect name="providerId" options={profileOptions} validate={required} includeOther otherName="otherProvider" />

      <FormLabel>Select Activity</FormLabel>
      <div className="form-radio__group">
        <FormRadio key="activity.pumping" name="activityType" value="pumping" validate={required} box>Pumping</FormRadio>
        <FormRadio key="activity.feeding" name="activityType" value="feeding" validate={required} box>Feeding</FormRadio>
        <FormRadio key="activity.diaper" name="activityType" value="diaper" validate={required} box>Diaper</FormRadio>
      </div>

      <Error name="activityType" />
      <Condition when="activityType" is="pumping"><Pumping /></Condition>
      <Condition when="activityType" is="feeding"><Feeding /></Condition>
      <Condition when="activityType" is="diaper"><Diaper /></Condition>

      <FormSpy subscription={{ submitting: true }}>
        {({ submitting }) => (
          <FormInputHolder>
            <SecondaryButton type="button" label="Cancel" onClick={toggleWarningModal} />
            <PrimaryButton type="submit" label="Save" disabled={submitting} />
          </FormInputHolder>
        )}
      </FormSpy>

      <FormSpy
        subscription={{ dirty: true, visited: true, values: true }}
        onChange={({ dirty, visited, values }) => {
          if (dirty) {
            setTimeout(() => {
              formContext.setValues(values);
              const actuallyDirty = Object.values(visited).some(Boolean);
              actuallyDirty ? formContext.setDirty() : formContext.notDirty();
            }, 0);
          }
        }}
      />
    </BaseForm>
  );
};

DietDiaperForm.propTypes = {
  babies: PropTypes.arrayOf(PropTypes.object),
  profileOptions: PropTypes.arrayOf(PropTypes.object),
  handleRecords: PropTypes.func,
  accountId: PropTypes.string,
  dietDiaperRecordsPost: PropTypes.func,
  dietDiaperRecordsPatch: PropTypes.func,
  dietDiaperRecordsList: PropTypes.func, 
  hideTrackerModals: PropTypes.func,
  selectedProfile: PropTypes.object,
  showFirstTimeSavingModal: PropTypes.bool,
  showSaveEntryModal: PropTypes.func,
  showUnSavedModal: PropTypes.func,
  selectedBabyId: PropTypes.string,
  dietDiaperModalShown: PropTypes.bool
};

export default DietDiaperForm;
